import { useEffect, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchInfraHealth, type InfraHealth } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/** Baytni odam-o'qiydigan ko'rinishga (KiB/MiB/GiB). */
function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—'
  const u = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`
}

/** Sekundni d/h/m ko'rinishга. */
function fmtUptime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '—'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/**
 * A2.05 — Infratuzilma dashboard'i. Go-process runtime + PostgreSQL sog'ligini
 * `GET /admin/infra` (read-only) dan ko'rsatadi: goroutine/heap/uptime, pgx pool
 * bandligi, DB hajmi/ulanishlar/versiya/migratsiya. PII yo'q — pg_stat snapshot.
 */
export function InfraDashboard() {
  const t = useT()
  const [data, setData] = useState<InfraHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchInfraHealth(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  const poolPct = data && data.db_pool.max > 0 ? (data.db_pool.total / data.db_pool.max) * 100 : 0

  return (
    <>
      <PageHeader
        title={t.infra.title}
        screenId="A2.05"
        description={t.infra.note}
        actions={
          <button
            onClick={() => setReload((n) => n + 1)}
            className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-xs text-secondary hover:bg-surface-3"
          >
            {t.table.refresh}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.infra.uptime} value={data ? fmtUptime(data.runtime.uptime_seconds) : undefined} loading={loading} accent />
          <KPI label={t.infra.goroutines} value={data?.runtime.goroutines} loading={loading} />
          <KPI label={t.infra.heap} value={data ? fmtBytes(data.runtime.heap_alloc) : undefined} loading={loading} />
          <KPI label={t.infra.dbSize} value={data ? fmtBytes(data.db.size_bytes) : undefined} loading={loading} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Panel title={t.infra.runtime}>
            {loading || !data ? (
              <RowsSkeleton />
            ) : (
              <dl className="divide-y divide-border-subtle">
                <Row k={t.infra.goVersion} v={data.runtime.go_version} mono />
                <Row k={t.infra.numCpu} v={String(data.runtime.num_cpu)} mono />
                <Row k={t.infra.goroutines} v={data.runtime.goroutines.toLocaleString()} mono />
                <Row k={t.infra.heapAlloc} v={fmtBytes(data.runtime.heap_alloc)} mono />
                <Row k={t.infra.heapSys} v={fmtBytes(data.runtime.heap_sys)} mono />
                <Row k={t.infra.gcCount} v={data.runtime.gc_count.toLocaleString()} mono />
              </dl>
            )}
          </Panel>

          <Panel title={t.infra.database}>
            {loading || !data ? (
              <RowsSkeleton />
            ) : (
              <>
                <dl className="divide-y divide-border-subtle">
                  <Row k={t.infra.pgVersion} v={data.db.pg_version} mono />
                  <Row k={t.infra.dbName} v={data.db.name} mono />
                  <Row k={t.infra.migration} v={`v${data.db.migration}`} mono />
                  <Row k={t.infra.activeConns} v={data.db.active_conns.toLocaleString()} mono />
                  <Row k={t.infra.dbSize} v={fmtBytes(data.db.size_bytes)} mono />
                </dl>
                <div className="mt-3 border-t border-border-subtle pt-3">
                  <div className="mb-1.5 flex items-center justify-between text-xs text-tertiary">
                    <span>{t.infra.pool}</span>
                    <span className="font-mono text-secondary">
                      {data.db_pool.total}/{data.db_pool.max} · {t.infra.idle} {data.db_pool.idle}
                    </span>
                  </div>
                  <div className="relative h-3 overflow-hidden rounded bg-surface-3">
                    <div
                      className={`h-full rounded ${poolPct > 85 ? 'bg-danger/70' : poolPct > 60 ? 'bg-warning/70' : 'bg-success/70'}`}
                      style={{ width: `${Math.max(2, Math.min(100, poolPct))}%` }}
                    />
                  </div>
                </div>
              </>
            )}
          </Panel>
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  accent,
}: {
  label: string
  value: number | string | undefined
  loading: boolean
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <dt className="shrink-0 text-tertiary">{k}</dt>
      <dd className={`min-w-0 truncate text-right text-primary ${mono ? 'font-mono text-xs tabular' : ''}`}>{v}</dd>
    </div>
  )
}

function RowsSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  )
}
